import type { KeyboardEvent } from "react";
import type { InlineComboboxOption } from "./inline-combobox-types";

export interface InlineComboboxKeyboardContext {
  open: boolean;
  navigableValues: readonly string[];
  activeValue: string | null;
  allOptions: readonly InlineComboboxOption[];
  openAndSeed: () => void;
  setQuery: (query: string) => void;
  setHighlightedValue: (value: string | null) => void;
  choose: (option: InlineComboboxOption) => void;
  close: () => void;
}

export function isTypeAheadKey(event: KeyboardEvent<HTMLElement>) {
  return event.key.length === 1 && !event.metaKey && !event.ctrlKey;
}

function handleClosedKey(
  event: KeyboardEvent<HTMLInputElement>,
  context: InlineComboboxKeyboardContext,
) {
  const isPrintable = isTypeAheadKey(event);
  if (event.key !== "ArrowDown" && event.key !== "Enter" && !isPrintable) {
    return;
  }
  event.preventDefault();
  context.openAndSeed();
  if (isPrintable) context.setQuery(event.key);
}

export function stepHighlight(
  navigableValues: readonly string[],
  activeValue: string | null,
  delta: number,
): string | null {
  const current = activeValue ? navigableValues.indexOf(activeValue) : 0;
  const next = Math.min(
    Math.max(current + delta, 0),
    navigableValues.length - 1,
  );
  return navigableValues[next] ?? null;
}

function moveHighlight(context: InlineComboboxKeyboardContext, delta: number) {
  if (context.navigableValues.length === 0) return;
  context.setHighlightedValue(
    stepHighlight(context.navigableValues, context.activeValue, delta),
  );
}

function chooseActive(context: InlineComboboxKeyboardContext) {
  const option = context.allOptions.find(
    (item) => item.value === context.activeValue,
  );
  if (option) context.choose(option);
}

function openKeyAction(
  key: string,
  context: InlineComboboxKeyboardContext,
): (() => void) | null {
  switch (key) {
    case "ArrowDown":
      return () => moveHighlight(context, 1);
    case "ArrowUp":
      return () => moveHighlight(context, -1);
    case "Enter":
      return () => chooseActive(context);
    case "Escape":
      return context.close;
    default:
      return null;
  }
}

export function handleInlineComboboxKey(
  event: KeyboardEvent<HTMLInputElement>,
  context: InlineComboboxKeyboardContext,
) {
  if (!context.open) {
    handleClosedKey(event, context);
    return;
  }
  const action = openKeyAction(event.key, context);
  if (!action) return;
  event.preventDefault();
  action();
}
